import { useState } from 'react'

import { resultant, ru } from '../../lib/physics'
import { Arrow, ArrowDefs, Choice, Predict, Slider } from './common'

/**
 * Сложение сил на динамометре (урок 38): к телу приложены две силы по одной прямой — вверх или
 * вниз. Сонаправленные складываются, противоположные вычитаются; равнодействующая направлена
 * к большей силе. Динамометр показывает равнодействующую.
 */

const PX_PER_N = 12
const ЦЕНТР = 140

export default function SumLab() {
  const [f1, setF1] = useState(5)
  const [f2, setF2] = useState(3)
  const [вниз2, setВниз2] = useState(false)

  const R = resultant([f1, вниз2 ? f2 : -f2]) // плюс — вниз
  const cx = 120

  return (
    <div className="card">
      <div className="lab-two">
        <svg className="lab-svg" style={{ maxWidth: 260, margin: '0 auto' }} viewBox="0 0 240 290" role="img" aria-label="Две силы и равнодействующая">
          <ArrowDefs />
          <rect x={cx - 20} y={ЦЕНТР - 16} width={40} height={32} rx={4} className="fill-body ink" strokeWidth={1.5} />
          <Arrow x1={cx - 30} y1={ЦЕНТР} x2={cx - 30} y2={ЦЕНТР + f1 * PX_PER_N} kind="heavy" label="F₁" labelDx={-22} />
          <Arrow x1={cx + 30} y1={ЦЕНТР} x2={cx + 30} y2={ЦЕНТР + (вниз2 ? f2 : -f2) * PX_PER_N} kind="el" label="F₂" />
          <Arrow x1={cx} y1={ЦЕНТР} x2={cx} y2={ЦЕНТР + R * PX_PER_N} kind="band" label="R" labelDx={8} labelDy={R >= 0 ? 12 : -2} />
        </svg>
        <div>
          <div className="lab-big">
            R = {ru(Math.abs(R), 1)} Н {Math.abs(R) < 1e-9 ? '— силы уравновешены' : R > 0 ? '↓ вниз' : '↑ вверх'}
          </div>
          <div className="lab-readout lab-readout-col">
            <span className="heavy">F₁ = {ru(f1, 1)} Н вниз</span>
            <span className="el">F₂ = {ru(f2, 1)} Н {вниз2 ? 'вниз' : 'вверх'}</span>
            <span className="band">
              {вниз2 ? `R = F₁ + F₂ = ${ru(f1, 1)} + ${ru(f2, 1)}` : `R = |F₁ − F₂| = |${ru(f1, 1)} − ${ru(f2, 1)}|`} = {ru(Math.abs(R), 1)} Н
            </span>
          </div>
        </div>
      </div>

      <Slider label="F₁ — груз тянет вниз" value={f1} min={0} max={8} step={0.5} unit="Н" shown={ru(f1, 1)} onChange={setF1} />
      <Slider label="F₂ — вторая сила" value={f2} min={0} max={8} step={0.5} unit="Н" shown={ru(f2, 1)} onChange={setF2} />
      <div className="lab-sub">Куда направлена F₂</div>
      <Choice options={[{ value: 0, label: '↑ вверх' }, { value: 1, label: '↓ вниз' }]} value={вниз2 ? 1 : 0} onChange={(v) => setВниз2(v === 1)} />

      <Predict
        question="Груз тянет вниз с силой 5 Н, а второй динамометр — вверх с силой 8 Н. Какова равнодействующая?"
        options={['13 Н вниз', '3 Н вверх', '3 Н вниз']}
        right={1}
        revealed={f1 === 5 && f2 === 8 && !вниз2}
        explain="Силы противоположны — вычитаем: 8 − 5 = 3 Н, и равнодействующая направлена к большей силе, вверх."
      />
      <p className="muted small">
        Равнодействующая — одна сила, которая действует так же, как несколько сил вместе. По одной
        прямой: в одну сторону — сумма, в разные — разность, направлена к большей.
      </p>
    </div>
  )
}
